import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { useState, useRef, useEffect } from 'react';
import { StyledInput } from './styles.js';
import { Flex } from '../Flex/Flex.js';
import Caption from '../Text/Caption/Caption.js';
import '../Text/DetailedSubheading/DetailedSubheading.js';
import '../Text/OverlineLg/OverlineLg.js';
import '../Text/OverlineMd/OverlineMd.js';
import '../Text/ParagraphLg/ParagraphLg.js';
import ParagraphMd from '../Text/ParagraphMd/ParagraphMd.js';
import '../Text/SectionHeading/SectionHeading.js';
import '../Text/SectionSubheading/SectionSubheading.js';
import '../Text/Title/Title.js';

const DEFAULT_ID = 'ds-input-verification-code';
function InputVerificationCode(_a) {
    var { ariaLabel = 'Por favor, digite o caractere de verificação na posição', errorMessage, error = false, inputType = 'number', label, codeLength = 6, onChange, shouldAutoFocus = false, value, required = true, id = DEFAULT_ID, disabled = false, helperText } = _a, props = __rest(_a, ["ariaLabel", "errorMessage", "error", "inputType", "label", "codeLength", "onChange", "shouldAutoFocus", "value", "required", "id", "disabled", "helperText"]);
    const [activeInput, setActiveInput] = useState(0);
    const inputRefs = useRef([]);
    const getCode = () => (value ? value.toString().split('') : []);
    const isInputNum = inputType === 'number' || inputType === 'tel';
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, codeLength);
    }, [codeLength]);
    useEffect(() => {
        var _a;
        if (shouldAutoFocus) {
            (_a = inputRefs.current[0]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [shouldAutoFocus]);
    const handleBlur = () => {
        setActiveInput(activeInput - 1);
    };
    const focusInput = (index) => {
        var _a, _b;
        const activeInput = Math.max(Math.min(codeLength - 1, index), 0);
        if (inputRefs.current[activeInput]) {
            (_a = inputRefs.current[activeInput]) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = inputRefs.current[activeInput]) === null || _b === void 0 ? void 0 : _b.select();
            setActiveInput(activeInput);
        }
    };
    const isInputValueValid = (value) => {
        const isTypeValid = isInputNum
            ? !Number.isNaN(Number(value))
            : typeof value === 'string';
        return isTypeValid && value.trim().length === 1;
    };
    const handleCodeChange = (code) => {
        const codeValue = code.join('');
        onChange(codeValue);
    };
    const changeCodeAtFocus = (value) => {
        const code = [...getCode()];
        [code[activeInput]] = value;
        handleCodeChange(code);
    };
    const handleChange = (event) => {
        const { value } = event.target;
        if (isInputValueValid(value)) {
            changeCodeAtFocus(value);
            focusInput(activeInput + 1);
        }
    };
    const handleInputChange = (event) => {
        const { nativeEvent } = event;
        if (!isInputValueValid(event.target.value)) {
            // @ts-expect-error - This was added to handle an edge case
            // for dealing with keyCode "229 Unidentified" on Android.
            // eslint-disable-next-line prettier/prettier
            if (nativeEvent.data === null && nativeEvent.inputType === 'deleteContentBackward') {
                event.preventDefault();
                changeCodeAtFocus('');
                focusInput(activeInput - 1);
            }
            // Clear the input if it's not valid value because firefox allows
            // pasting non-numeric characters in a number type input
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';
        }
    };
    const handleFocus = (event) => (index) => {
        setActiveInput(index);
        event.target.select();
    };
    const handleKeyDown = (event) => {
        const code = getCode();
        if ([event.code, event.key].includes('Backspace')) {
            event.preventDefault();
            changeCodeAtFocus('');
            focusInput(activeInput - 1);
        }
        else if (event.code === 'Delete') {
            event.preventDefault();
            changeCodeAtFocus('');
        }
        else if (event.code === 'ArrowLeft') {
            event.preventDefault();
            focusInput(activeInput - 1);
        }
        else if (event.code === 'ArrowRight') {
            event.preventDefault();
            focusInput(activeInput + 1);
        }
        // React does not trigger onChange when the same value is entered
        // again. So we need to focus the next input manually in this case.
        else if (event.key === code[activeInput]) {
            event.preventDefault();
            focusInput(activeInput + 1);
        }
        else if (event.code === 'Spacebar' ||
            event.code === 'Space' ||
            event.code === 'ArrowUp' ||
            event.code === 'ArrowDown') {
            event.preventDefault();
        }
    };
    const handlePaste = (event) => {
        var _a;
        event.preventDefault();
        const code = getCode();
        let nextActiveInput = activeInput;
        // Get pastedData in an array of max size (num of inputs - current position)
        const pastedData = event.clipboardData
            .getData('text/plain')
            .slice(0, codeLength - activeInput)
            .split('');
        // Prevent pasting if the clipboard data contains non-numeric values for number inputs
        if (isInputNum && pastedData.some((value) => Number.isNaN(Number(value)))) {
            return;
        }
        // Paste data from focused input onwards
        for (let pos = 0; pos < codeLength; pos += 1) {
            if (pos >= activeInput && pastedData.length > 0) {
                code[pos] = (_a = pastedData.shift()) !== null && _a !== void 0 ? _a : '';
                nextActiveInput += 1;
            }
        }
        focusInput(nextActiveInput);
        handleCodeChange(code);
    };
    return (React.createElement(Flex, Object.assign({}, props, { flexDirection: "column" }),
        label && (React.createElement(ParagraphMd, { bold: true, as: "label", htmlFor: id }, label)),
        React.createElement(Flex, { alignItems: "center", gap: "inline.3xsmall", id: id }, Array.from({ length: codeLength }, (_, index) => index).map((index) => {
            var _a;
            return (React.createElement(StyledInput, { role: "textbox", required: required, key: index, value: (_a = getCode()[index]) !== null && _a !== void 0 ? _a : '', ref: (element) => {
                    inputRefs.current[index] = element;
                }, onChange: handleChange, onFocus: (event) => handleFocus(event)(index), onBlur: handleBlur, onKeyDown: handleKeyDown, onPaste: handlePaste, autoComplete: "off", maxLength: 1, "aria-label": `${ariaLabel} ${index + 1}`, type: inputType, inputMode: isInputNum ? 'numeric' : 'text', onInput: handleInputChange, feedback: error ? 'error' : undefined, showFeedbackIcon: false, disabled: disabled }));
        })),
        errorMessage && error && (React.createElement(Caption, { c: "neutral.stronger", mt: "stack.3xsmall" }, errorMessage)),
        !error && helperText && (React.createElement(Caption, { c: "neutral.stronger", mt: "stack.3xsmall" }, helperText))));
}

export { InputVerificationCode };
